import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Globe, ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import tamLogo from '@/assets/tam-logo.png';

interface SiteFile {
  name: string;
  content: string;
  language: string;
  isImage?: boolean;
}

interface HostedSiteData {
  id: string;
  slug: string;
  site_name: string;
  description: string | null;
  files: SiteFile[];
  views_count: number;
  created_at: string;
}

const HostedSite = () => {
  const { slug } = useParams<{ slug: string }>();
  const [site, setSite] = useState<HostedSiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch site data
  useEffect(() => {
    if (!slug) return;

    const fetchSite = async () => {
      setLoading(true);
      try {
      const { data, error: fetchError } = await (supabase as any)
          .from('hosted_sites')
          .select('id, slug, site_name, description, files, views_count, created_at')
          .eq('slug', slug)
          .eq('is_published', true)
          .maybeSingle();

        if (fetchError) throw fetchError;
        if (!data) {
          setError('Site not found');
          return;
        }

        setSite({
          ...data,
          files: (data.files as any) || [],
        });

        // Track visit
        trackVisit(data.id);
      } catch (e: any) {
        console.error('Error fetching site:', e);
        setError(e.message || 'Failed to load site');
      } finally {
        setLoading(false);
      }
    };

    fetchSite();
  }, [slug]);

  const trackVisit = async (siteId: string) => {
    try {
      const ua = navigator.userAgent;
      let deviceType = 'desktop';
      if (/Mobile|Android|iPhone/i.test(ua)) deviceType = 'mobile';
      else if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';

      let browser = 'other';
      if (ua.includes('Chrome') && !ua.includes('Edge')) browser = 'Chrome';
      else if (ua.includes('Firefox')) browser = 'Firefox';
      else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
      else if (ua.includes('Edge')) browser = 'Edge';

      let os = 'other';
      if (ua.includes('Windows')) os = 'Windows';
      else if (ua.includes('Mac')) os = 'macOS';
      else if (ua.includes('Linux')) os = 'Linux';
      else if (ua.includes('Android')) os = 'Android';
      else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

      await supabase.functions.invoke('track-visit', {
        body: {
          siteId,
          referrer: document.referrer || null,
          deviceType,
          browser,
          os,
          screenSize: `${window.screen.width}x${window.screen.height}`,
          pagePath: '/',
        },
      });
    } catch (e) {
      console.error('Failed to track visit:', e);
    }
  };

  // Build preview HTML from files
  const previewContent = useMemo(() => {
    if (!site?.files) return '';

    const files = site.files;
    const htmlFile = files.find(f => f.name === 'index.html');
    const cssFile = files.find(f => f.name === 'style.css');
    const jsFile = files.find(f => f.name === 'script.js');

    if (!htmlFile) return '<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif;color:#888;background:#1a1a2e;"><p>No index.html found</p></body></html>';

    let html = htmlFile.content;

    // Inject CSS
    if (cssFile) {
      const styleTag = `<style>${cssFile.content}</style>`;
      if (html.includes('</head>')) {
        html = html.replace('</head>', `${styleTag}</head>`);
      } else {
        html = `<head>${styleTag}</head>${html}`;
      }
    }
    html = html.replace(/<link[^>]*href=["']style\.css["'][^>]*>/gi, '');

    // Inject JS
    if (jsFile) {
      html = html.replace(/<script[^>]*src=["']script\.js["'][^>]*><\/script>/gi, '');
      const scriptTag = `<script>${jsFile.content}</script>`;
      if (html.includes('</body>')) {
        html = html.replace('</body>', `${scriptTag}</body>`);
      } else {
        html = `${html}${scriptTag}`;
      }
    }

    // Replace image references with base64
    files.forEach(file => {
      if (file.isImage && file.content?.startsWith('data:')) {
        const regex = new RegExp(`src=["']${file.name}["']`, 'gi');
        html = html.replace(regex, `src="${file.content}"`);
      }
    });

    return html;
  }, [site]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
          <p className="text-gray-400 text-sm">Loading site...</p>
        </div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
            <Globe className="h-8 w-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Site Not Found</h1>
          <p className="text-gray-400 text-sm">
            The site "{slug}" doesn't exist or has been unpublished.
          </p>
          <Link
            to="/dashboard/tools/code-studio"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
          >
            Create Your Own Site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#0a0a1a]">
      {/* Full-screen iframe */}
      <div className="flex-1 min-h-0">
        <iframe
          srcDoc={previewContent}
          title={site.site_name}
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-modals allow-popups allow-forms"
        />
      </div>

      {/* Minimal branding bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#0d0d20] border-t border-white/5">
        <div className="flex items-center gap-2">
          <img src={tamLogo} alt="TAM" className="h-5 w-5 rounded-full" />
          <span className="text-xs text-gray-400">
            Hosted on <span className="text-white font-medium">TAM Store</span>
          </span>
          <span className="text-gray-600 text-xs">•</span>
          <span className="text-xs text-gray-500">{site.site_name}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{site.views_count.toLocaleString()} views</span>
          <Link
            to="/dashboard/tools/code-studio"
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
          >
            Create yours <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HostedSite;
